// The bid screen's smaller pieces (Lane 1331, Phase 2 slice 2B; new look Lane
// 1394): the window frame, the receipt line, the history list, the Excel
// upload check step and result, and the withdraw and decline windows.
// Withdraw and decline each confirm before they send, and keep one
// Idempotency-Key per action so a retry after a lost connection can never act
// twice.
import React, { useEffect, useRef, useState } from 'react';
import { portalApi, newActionKey } from './portalApi';
import { formatDueAt, formatUnitPrice } from './portalView';
import { shortReceipt } from './bidModel';

const REASON_MAX = 1000;

// A connection failure or a server error may have landed: retry with the same
// key. Any other refusal is final for that key.
const mayRetry = (e) => e.status === 0 || e.status >= 500;

// A window over the page. Escape and the close button call `onClose` unless
// `busy` (a send is running). Focus moves into the window when it opens.
export function Modal({ title, onClose, busy = false, wide = false, footer, children }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.focus(); }, []);
  const close = () => { if (!busy && onClose) onClose(); };
  return (
    <div className="srcp-overlay" onKeyDown={(e) => { if (e.key === 'Escape') close(); }}>
      <div ref={ref} tabIndex={-1} className={`srcp-modal${wide ? ' srcp-modal--wide' : ''}`}
        role="dialog" aria-modal="true" aria-label={title}>
        <div className="srcp-modal-h">
          <span className="srcp-card-t">{title}</span>
          {onClose && <button type="button" className="srcp-x" aria-label="Close" disabled={busy} onClick={close}>×</button>}
        </div>
        <div className="srcp-modal-b">{children}</div>
        {footer && <div className="srcp-modal-f">{footer}</div>}
      </div>
    </div>
  );
}

export function Banner({ tone = '', children, actions = null, role }) {
  return (
    <div className={`srcp-banner${tone ? ` srcp-banner--${tone}` : ''}`} role={role}>
      <div className="srcp-grow">{children}</div>
      {actions}
    </div>
  );
}

export function Receipt({ receipt, onClose }) {
  if (!receipt) return null;
  return (
    <Banner tone="ok" role="status"
      actions={<button type="button" className="srcp-btn srcp-btn--sm" onClick={onClose}>Close</button>}>
      <div className="srcp-strong">{receipt.title}</div>
      {receipt.lines.map((line, i) => <div key={i}>{line}</div>)}
    </Banner>
  );
}

export function submitReceipt(r) {
  const lines = [
    `Sent ${formatDueAt(r.submitted_at)}. ${r.priced} priced and ${r.no_bid} no-bid parts.`,
    `Receipt ${shortReceipt(r.receipt)}`,
  ];
  if (r.valid_until) lines.splice(1, 0, `Your prices are valid until ${formatDueAt(r.valid_until)}.`);
  return {
    title: r.replayed ? 'This submission was already received.' : 'Your quote was sent to the buyer.',
    lines,
  };
}

function historyWhat(h) {
  if (h.kind === 'submission' && h.source === 'buyer_edit') return 'Price changed by the buyer';
  if (h.kind === 'submission') return h.source === 'buyer_file' ? 'Entered by the buyer from your file' : 'Submitted';
  if (h.kind === 'withdrawal' && h.cause === 'decline') return 'Declined. All prices withdrawn.';
  return 'Withdrew prices';
}

export function HistoryPanel({ history }) {
  return (
    <section className="srcp-card">
      <div className="srcp-card-h">
        <span className="srcp-card-t">Your submissions</span>
        <span className="srcp-mut">Each line has a receipt number you can quote to the buyer.</span>
      </div>
      {(!history || history.length === 0) && (
        <div className="srcp-card-b srcp-mut">You have not submitted anything yet.</div>
      )}
      {history && history.length > 0 && (
        <div className="srcp-scroll">
          <table className="srcp-grid">
            <thead>
              <tr><th>When</th><th>What</th><th className="srcp-r">Round</th><th>Parts</th><th>Receipt</th></tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={`${h.kind}-${h.id}`}>
                  <td>{formatDueAt(h.at)}</td>
                  <td>{historyWhat(h)}</td>
                  <td className="srcp-r srcp-num">{h.round || ''}</td>
                  <td>{h.kind === 'submission' ? `${h.priced} priced, ${h.no_bid} no bid` : ''}</td>
                  <td className="srcp-mono" title={h.receipt_digest || ''}>{shortReceipt(h.receipt_digest)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ProblemRows({ rows }) {
  return (
    <div className="srcp-scroll">
      <table className="srcp-grid">
        <thead><tr><th className="srcp-r">Row</th><th>Part number</th><th>Problem</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.row}-${i}`}>
              <td className="srcp-r srcp-num">{r.row}</td>
              <td className="srcp-mono">{r.part_number || ''}</td>
              <td className="srcp-ell srcp-ell--wide" title={r.reason}>{r.reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The result of Apply: what went into the draft and the rows that did not.
export function UploadResult({ upload, onClose }) {
  if (!upload || upload.busy) return null;
  const rows = upload.errors || [];
  return (
    <Banner tone={upload.error ? 'bad' : 'ok'} role={upload.error ? 'alert' : 'status'}
      actions={<button type="button" className="srcp-btn srcp-btn--sm" onClick={onClose}>Close</button>}>
      {upload.error && <div>{upload.error}</div>}
      {upload.result && (
        <div className="srcp-strong">
          {upload.result.saved_parts.toLocaleString()} {upload.result.saved_parts === 1 ? 'part is' : 'parts are'} in
          your draft. Nothing is sent to the buyer until you press Submit quote.
        </div>
      )}
      {rows.length > 0 && (
        <>
          <div>{rows.length === 1 ? 'One row was not saved.' : `${rows.length.toLocaleString()} rows were not saved.`}</div>
          <ProblemRows rows={rows} />
        </>
      )}
    </Banner>
  );
}

// ---- The Excel upload check step (Lane 1394, decision D1) -----------------------------

const asNum = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const sameNum = (a, b) => (a === null || b === null ? a === b : a === b);

// One row of the check: the part's DRAFT row against the file (Codex slice B
// finding 1: never the submitted offer, so a file that repeats a submitted
// price still goes into the draft and can be submitted again, which renews
// it). `now` is the draft row { price, lead, moq, no_bid, note } with numbers
// or null; `submitted` is the offer that counts now ({ price, no_bid, text })
// or null; `file` is the server's parsed row.
export function compareImportRow(now, file, submitted = null) {
  const f = {
    price: asNum(file.price), lead: asNum(file.lead_time_days), moq: asNum(file.min_order_units),
    no_bid: Boolean(file.no_bid), note: (file.note || '').trim(),
  };
  const nowEmpty = now.price === null && !now.no_bid && now.lead === null && now.moq === null && !now.note;
  const same = sameNum(now.price, f.price) && sameNum(now.lead, f.lead) && sameNum(now.moq, f.moq)
    && now.no_bid === f.no_bid && now.note === f.note;
  const sameAsSubmitted = Boolean(submitted) && nowEmpty && submitted.no_bid === f.no_bid
    && sameNum(submitted.price, f.price);
  let change = 'Changed';
  if (same) change = 'No change';
  else if (sameAsSubmitted) change = submitted.text;
  else if (f.no_bid) change = 'No bid';
  else if (nowEmpty) change = 'New';
  return {
    change,
    differs: {
      price: !sameNum(now.price, f.price) || now.no_bid !== f.no_bid,
      lead: !sameNum(now.lead, f.lead), moq: !sameNum(now.moq, f.moq), note: now.note !== f.note,
    },
  };
}

function Tile({ k, v, tone }) {
  return (
    <div className="srcp-tile">
      <span className="srcp-tile-k">{k}</span>
      <span className={`srcp-tile-v${tone ? ` srcp-tile-v--${tone}` : ''}`}>{v}</span>
    </div>
  );
}

// `review`: { fileName, parts, errors } from a preview, or { fileName, refused,
// rows } for a file the server refuses. `nowOf(pn)` gives { draft, submitted }:
// the part's draft row and the offer that counts now (see compareImportRow).
export function UploadReview({ review, nowOf, currency, applying, onApply, onCancel, onDownload }) {
  const [show, setShow] = useState('changes');
  if (review.refused) {
    return (
      <section className="srcp-card">
        <div className="srcp-card-h">
          <span className="srcp-card-t">Check your file</span>
          <span className="srcp-mut srcp-mono">{review.fileName}</span>
        </div>
        <div className="srcp-card-b">
          <Banner tone="bad" role="alert">{review.refused} Nothing in your draft changed.</Banner>
          {review.rows && review.rows.length > 0 && <ProblemRows rows={review.rows} />}
          <div className="srcp-row">
            <button type="button" className="srcp-btn srcp-btn--primary" onClick={onDownload}>Download a new file</button>
            <button type="button" className="srcp-btn" onClick={onCancel}>Back to your quote</button>
          </div>
        </div>
      </section>
    );
  }
  const price = (v, noBid) => (noBid ? 'No bid' : (v === null || v === undefined ? '' : formatUnitPrice(v, currency)));
  const rows = review.parts.map((p) => {
    const { draft: now, submitted } = nowOf(p.part_number);
    return { part: p, now, ...compareImportRow(now, p, submitted) };
  });
  const changes = rows.filter((r) => r.change !== 'No change');
  const shown = show === 'all' ? rows : changes;
  const problems = review.errors || [];
  return (
    <>
      <section className="srcp-card">
        <div className="srcp-card-h">
          <span className="srcp-card-t">Check your file before it goes into your draft</span>
          <span className="srcp-mut srcp-mono">{review.fileName}</span>
        </div>
        <div className="srcp-tiles">
          <Tile k="Parts in the file" v={rows.length.toLocaleString()} />
          <Tile k="Changes found" v={changes.length.toLocaleString()} />
          <Tile k="Rows with a problem" v={problems.length.toLocaleString()} tone={problems.length ? 'warn' : ''} />
          <Tile k="No change" v={(rows.length - changes.length).toLocaleString()} />
        </div>
      </section>
      <Banner>
        Nothing is sent to the buyer. Apply puts these changes in your draft. Rows with a problem are skipped.
        You still press Submit quote to send.
      </Banner>
      <section className="srcp-card">
        <div className="srcp-toolbar">
          <span className="srcp-card-t">What changed, part by part</span>
          <span className="srcp-grow" />
          <div className="srcp-chips" role="group" aria-label="Rows to show">
            <button type="button" aria-pressed={show === 'changes'} className={`srcp-chip${show === 'changes' ? ' srcp-chip--on' : ''}`}
              onClick={() => setShow('changes')}>Changes {changes.length.toLocaleString()}</button>
            <button type="button" aria-pressed={show === 'all'} className={`srcp-chip${show === 'all' ? ' srcp-chip--on' : ''}`}
              onClick={() => setShow('all')}>All rows {rows.length.toLocaleString()}</button>
          </div>
        </div>
        {shown.length === 0 && <div className="srcp-card-b srcp-mut">No changes. Your draft already has what the file says.</div>}
        {shown.length > 0 && (
          <div className="srcp-scroll">
            <table className="srcp-grid">
              <thead>
                <tr>
                  <th className="srcp-sticky">Part number</th>
                  <th className="srcp-r">Price in draft</th><th className="srcp-r">Price in file</th>
                  <th className="srcp-r">Lead time in draft</th><th className="srcp-r">Lead time in file</th>
                  <th className="srcp-r">Minimum order in draft</th><th className="srcp-r">Minimum order in file</th>
                  <th>Note in file</th><th>Change</th>
                </tr>
              </thead>
              <tbody>
                {shown.map(({ part: p, now, change, differs }) => (
                  <tr key={p.part_number}>
                    <td className="srcp-sticky srcp-mono">{p.part_number}</td>
                    <td className="srcp-r srcp-num srcp-notes">{price(now.price, now.no_bid)}</td>
                    <td className={`srcp-r srcp-num${differs.price ? ' srcp-chg' : ''}`}>{price(p.price, p.no_bid)}</td>
                    <td className="srcp-r srcp-num srcp-notes">{now.lead === null ? '' : now.lead.toLocaleString()}</td>
                    <td className={`srcp-r srcp-num${differs.lead ? ' srcp-chg' : ''}`}>{asNum(p.lead_time_days) === null ? '' : asNum(p.lead_time_days).toLocaleString()}</td>
                    <td className="srcp-r srcp-num srcp-notes">{now.moq === null ? '' : now.moq.toLocaleString()}</td>
                    <td className={`srcp-r srcp-num${differs.moq ? ' srcp-chg' : ''}`}>{asNum(p.min_order_units) === null ? '' : asNum(p.min_order_units).toLocaleString()}</td>
                    <td className={`srcp-ell${differs.note ? ' srcp-chg' : ''}`} title={p.note || ''}>{p.note || ''}</td>
                    <td><span className={`srcp-pill${change === 'No change' ? '' : ' srcp-pill--info'}`}>{change}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {problems.length > 0 && (
        <section className="srcp-card">
          <div className="srcp-card-h">
            <span className="srcp-card-t">Rows we skip</span>
            <span className="srcp-mut">Fix these in the file and upload it again, or enter them on the screen.</span>
          </div>
          <ProblemRows rows={problems} />
        </section>
      )}
      <div className="srcp-row">
        <button type="button" className="srcp-btn srcp-btn--primary" disabled={applying || changes.length === 0}
          onClick={onApply}>
          {applying ? 'Applying…' : `Apply ${changes.length.toLocaleString()} ${changes.length === 1 ? 'change' : 'changes'}`}
        </button>
        <button type="button" className="srcp-btn" disabled={applying} onClick={onCancel}>Cancel</button>
        <span className="srcp-mut">Cancel leaves your draft as it is.</span>
      </div>
    </>
  );
}

// One keyed action with a confirm step: the form, then "Are you sure", then
// the send. The key is made at the confirm step and kept for a retry of that
// same send.
function useKeyedAction() {
  const keyRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (send, onDone) => {
    if (!keyRef.current) keyRef.current = newActionKey();
    setBusy(true);
    setError('');
    try {
      const result = await send(keyRef.current);
      keyRef.current = null;
      onDone(result);
    } catch (e) {
      if (e.status === 401) return;
      if (mayRetry(e)) {
        setError(`${e.message} It is safe to try again.`);
      } else {
        keyRef.current = null;
        setError(e.message);
      }
    } finally {
      setBusy(false);
    }
  };
  const reset = () => { keyRef.current = null; setError(''); };
  return { busy, error, run, reset };
}

function ReasonField({ id, label, value, onChange, disabled, placeholder }) {
  return (
    <div className="srcp-field">
      <label className="srcp-label" htmlFor={id}>{label}</label>
      <textarea id={id} className="srcp-input srcp-textarea" rows={3} maxLength={REASON_MAX}
        value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

// `submitted`: [{ part_number, text }] for every part with a submitted price or no bid.
export function WithdrawPanel({ eventId, submitted, onDone, onCancel }) {
  const [mode, setMode] = useState('all');
  const [chosen, setChosen] = useState(() => new Set());
  const [filterText, setFilterText] = useState('');
  const [reason, setReason] = useState('');
  const [step, setStep] = useState('form');
  const action = useKeyedAction();

  const edit = (fn) => { action.reset(); setStep('form'); fn(); };
  const toggle = (pn) => edit(() => setChosen((prev) => {
    const next = new Set(prev);
    if (next.has(pn)) next.delete(pn); else next.add(pn);
    return next;
  }));
  const count = mode === 'all' ? submitted.length : chosen.size;
  const ready = reason.trim() && count > 0;
  const needle = filterText.trim().toLowerCase();
  const shown = needle ? submitted.filter((s) => s.part_number.toLowerCase().includes(needle)) : submitted;

  const send = () => action.run(
    (key) => portalApi.withdraw(eventId, {
      partNumbers: mode === 'all' ? undefined : [...chosen], reason: reason.trim(),
    }, key),
    onDone,
  );

  const footer = step === 'confirm' ? (
    <>
      <button type="button" className="srcp-btn srcp-btn--primary" disabled={action.busy} onClick={send}>
        {action.busy ? 'Withdrawing…' : 'Yes, withdraw'}
      </button>
      <button type="button" className="srcp-btn" disabled={action.busy} onClick={() => setStep('form')}>Back</button>
    </>
  ) : (
    <>
      <button type="button" className="srcp-btn srcp-btn--primary" disabled={!ready}
        onClick={() => setStep('confirm')}>Withdraw</button>
      <button type="button" className="srcp-btn" onClick={onCancel}>Cancel</button>
    </>
  );

  return (
    <Modal title="Withdraw prices" onClose={onCancel} busy={action.busy} footer={footer}>
      <div className="srcp-mut">The buyer stops seeing the prices you withdraw. You can send new prices while the round is open.</div>
      <div className="srcp-row">
        <label className="srcp-choice">
          <input type="radio" name="withdraw-mode" checked={mode === 'all'} disabled={action.busy}
            onChange={() => edit(() => setMode('all'))} />
          All {submitted.length.toLocaleString()} submitted parts
        </label>
        <label className="srcp-choice">
          <input type="radio" name="withdraw-mode" checked={mode === 'choose'} disabled={action.busy}
            onChange={() => edit(() => setMode('choose'))} />
          Choose parts
        </label>
      </div>
      {mode === 'choose' && (
        <>
          <input className="srcp-input" value={filterText} maxLength={100}
            placeholder="Find a part number" aria-label="Find a submitted part"
            onChange={(e) => setFilterText(e.target.value)} />
          <div className="srcp-picklist">
            {shown.map((s) => (
              <label key={s.part_number} className="srcp-choice">
                <input type="checkbox" checked={chosen.has(s.part_number)} disabled={action.busy}
                  onChange={() => toggle(s.part_number)} />
                <span className="srcp-mono">{s.part_number}</span>
                <span className="srcp-grow" />
                <span className="srcp-mut">{s.text}</span>
              </label>
            ))}
            {shown.length === 0 && <div className="srcp-mut srcp-card-b">No submitted parts match.</div>}
          </div>
        </>
      )}
      <ReasonField id="withdraw-reason" label="Reason (the buyer sees this)" value={reason}
        disabled={action.busy} onChange={(v) => edit(() => setReason(v))} />
      {step === 'confirm' && (
        <div className="srcp-confirm">
          Withdraw {mode === 'all' ? 'every price you submitted' : `${count.toLocaleString()} ${count === 1 ? 'part' : 'parts'}`}?
          This cannot be undone. To quote again, enter new prices and submit.
        </div>
      )}
      {action.error && <div className="srcp-error" role="alert">{action.error}</div>}
    </Modal>
  );
}

export function DeclinePanel({ eventId, submittedCount, onDone, onCancel }) {
  const [reason, setReason] = useState('');
  const [step, setStep] = useState('form');
  const action = useKeyedAction();
  const send = () => action.run((key) => portalApi.decline(eventId, reason.trim(), key), onDone);

  const footer = step === 'confirm' ? (
    <>
      <button type="button" className="srcp-btn srcp-btn--primary" disabled={action.busy} onClick={send}>
        {action.busy ? 'Declining…' : 'Yes, decline'}
      </button>
      <button type="button" className="srcp-btn" disabled={action.busy} onClick={() => setStep('form')}>Back</button>
    </>
  ) : (
    <>
      <button type="button" className="srcp-btn srcp-btn--primary" disabled={!reason.trim()}
        onClick={() => setStep('confirm')}>Decline</button>
      <button type="button" className="srcp-btn" onClick={onCancel}>Cancel</button>
    </>
  );

  return (
    <Modal title="Decline this event" onClose={onCancel} busy={action.busy} footer={footer}>
      <ReasonField id="decline-reason" label="Reason (the buyer sees this)" value={reason}
        placeholder="For example: our capacity is full for next year."
        disabled={action.busy} onChange={(v) => { action.reset(); setStep('form'); setReason(v); }} />
      {step === 'confirm' && (
        <div className="srcp-confirm">
          Declining withdraws every price you submitted in this event
          {submittedCount > 0 ? ` (${submittedCount.toLocaleString()} ${submittedCount === 1 ? 'part' : 'parts'})` : ''} and
          clears your draft. The buyer sees that you declined. You can still send a new quote while the round is open.
        </div>
      )}
      {action.error && <div className="srcp-error" role="alert">{action.error}</div>}
    </Modal>
  );
}
